import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking and structured logging.
 * Call this function before rendering the React app.
 */
export function initSentry() {
  const dsn = import.meta.env.REACT_APP_SENTRY_DSN;

  console.log('[Sentry] Initializing with DSN:', dsn ? 'configured' : 'MISSING');

  // Only initialize if DSN is configured
  if (!dsn) {
    console.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  const environment = import.meta.env.REACT_APP_ENVIRONMENT || 'development';
  const release = import.meta.env.REACT_APP_VERSION || 'systemdox-react@0.1.0';
  const isDebugMode = import.meta.env.REACT_APP_SENTRY_DEBUG === 'true';

  console.log('[Sentry] Environment:', environment, 'Release:', release, 'Debug:', isDebugMode);

  // Filter out noisy errors (skip in debug mode to capture everything)
  const ignoreErrors = isDebugMode ? [] : [
    // Browser extensions
    'top.GLOBALS',
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    // Network errors (usually user's connection)
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    // User cancelled
    'AbortError',
  ];

  Sentry.init({
    dsn,
    environment,
    release,
    sendDefaultPii: true,

    // Structured logging — sends logs to Sentry Logs product
    // Logs are sent via explicit Sentry.logger.* calls in logger.ts
    enableLogs: true,

    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session replay for debugging (optional, can be disabled)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === 'production' ? 1.0 : 0,

    // Filter out noisy errors (empty array in debug mode)
    ignoreErrors,

    // Don't send errors from browser extensions
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // Attach additional context
    beforeSend(event, hint) {
      // In debug mode, log events before sending
      if (isDebugMode) {
        console.log('Sentry event (debug mode):', event);
      }
      // In development without debug mode, don't send events
      if (environment === 'development' && !isDebugMode) {
        return null;
      }
      return event;
    },
  });

  // Set initial user context if available
  const userId = localStorage.getItem('userId');
  if (userId) {
    Sentry.setUser({ id: userId });
  }
}

/**
 * Set the current user for Sentry tracking
 */
export function setSentryUser(user: { id: string; email?: string; username?: string } | null) {
  if (user) {
    Sentry.setUser(user);
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture a custom error with additional context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a custom message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Re-export Sentry's ErrorBoundary for convenience
export const SentryErrorBoundary = Sentry.ErrorBoundary;
